import { validateForm } from '@funkit/connect-core'
import type {
  FormDescriptor,
  FormField,
  FormFieldIssue,
  FormValues,
  JsonValue,
} from '@funkit/connect-core'

export type KycFieldIssue = FormFieldIssue | 'DATE_UNDERAGE'

/** Known validator codes, narrowed from TanStack's unknown field errors. */
const KYC_FIELD_ISSUES: ReadonlySet<KycFieldIssue> = new Set([
  'REQUIRED',
  'PATTERN',
  'COUNTRY_CODE',
  'DATE_FORMAT',
  'DATE_FUTURE',
  'DATE_UNDERAGE',
])

const isKycFieldIssue = (error: unknown): error is KycFieldIssue =>
  typeof error === 'string' && KYC_FIELD_ISSUES.has(error as KycFieldIssue)

/**
 * The first refusal a field carries, or nothing.
 *
 * A field can hold the same issue under two validation causes at once (mount
 * and change both mark a blank required field); the row draws one colour, so
 * one is all it needs.
 */
export function firstIssue(
  errors: readonly unknown[] | undefined,
): KycFieldIssue | undefined {
  return errors?.find(isKycFieldIssue)
}

/** Every control on this screen answers with text; anything else reads blank. */
export const stringValue = (value: JsonValue | undefined): string =>
  typeof value === 'string' ? value : ''

/**
 * What the screen's form holds: one string per field.
 *
 * Flat on purpose. `JsonValue` is recursive, and TanStack derives its field
 * paths from the data type, so the recursion has no floor for the compiler to
 * stand on. Nothing here answers with anything but text anyway.
 */
export type KycFormValues = Record<string, string>

/** The flow's values as the form takes them; anything not text reads blank. */
export function typedValues(values: FormValues): KycFormValues {
  return Object.fromEntries(
    Object.entries(values).map(([fieldId, value]) => [
      fieldId,
      stringValue(value),
    ]),
  )
}

/**
 * The COUNTRY fields the buyer's region can answer for them: the blank ones.
 * A value they typed is theirs, whatever the lookup says.
 */
export function blankCountryFields(
  fields: readonly FormField[],
  values: FormValues,
): FormField[] {
  return fields.filter(
    (field) => field.type === 'COUNTRY' && stringValue(values[field.id]) === '',
  )
}

/**
 * The fields an earlier round in this session already answered: blank here,
 * non-blank there.
 *
 * Rails key their own records differently from ours — Stripe puts the name and
 * phone from `registerLinkUser` on the Link consumer account, not on the crypto
 * customer, so the identity round genuinely has to send the name again. That is
 * a reason to re-send it, not a reason to make the buyer retype it.
 */
export function carriedBlankFields(
  fields: readonly FormField[],
  values: FormValues,
  carried: FormValues,
): FormField[] {
  return fields.filter(
    (field) =>
      // Untrimmed on this round, like `blankCountryFields`: whitespace the
      // buyer typed is still theirs. Trimmed on the held side, because
      // carrying it forward would dress a required field as answered and then
      // fail its validation with nothing on screen to explain why.
      stringValue(values[field.id]) === '' &&
      stringValue(carried[field.id]).trim() !== '',
  )
}

/** Every rail this form feeds requires an adult (design 2114:8361). */
export const MINIMUM_AGE_YEARS = 18

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/

/**
 * Whether a date of birth makes the buyer too young to continue.
 *
 * Only a well-formed past date is judged: a malformed one is connect-core's
 * `DATE_FORMAT`, and a future one its `DATE_FUTURE`, both worded more
 * precisely than "too young". Compared as UTC days so a clock behind UTC
 * cannot fail someone on their birthday.
 */
export function underageIssue(
  value: string,
  today: Date = new Date(),
): 'DATE_UNDERAGE' | undefined {
  const parts = ISO_DATE.exec(value)
  if (parts === null) {
    return undefined
  }
  const [, year, month, day] = parts
  const birthday = Date.UTC(Number(year), Number(month) - 1, Number(day))
  const now = Date.UTC(
    today.getUTCFullYear(),
    today.getUTCMonth(),
    today.getUTCDate(),
  )
  // `Date.UTC` rolls a day the calendar lacks forward (Feb 30th to March
  // 1st), which is a malformed date, not an age.
  if (!isCalendarDay(birthday, month, day) || birthday > now) {
    return undefined
  }
  const comesOfAge = Date.UTC(
    Number(year) + MINIMUM_AGE_YEARS,
    Number(month) - 1,
    Number(day),
  )
  return comesOfAge > now ? 'DATE_UNDERAGE' : undefined
}

function isCalendarDay(epochMs: number, month: string, day: string): boolean {
  const date = new Date(epochMs)
  return (
    !Number.isNaN(epochMs) &&
    date.getUTCMonth() === Number(month) - 1 &&
    date.getUTCDate() === Number(day)
  )
}

/** Keep every rule alive when its field's page is unmounted. */
export function validateKycForm(
  form: FormDescriptor,
  values: FormValues,
): Record<string, KycFieldIssue> {
  const issues: Record<string, KycFieldIssue> = validateForm(form, values)
  for (const field of form.fields) {
    if (field.type !== 'DATE' || issues[field.id] !== undefined) {
      continue
    }
    const issue = underageIssue(stringValue(values[field.id]))
    if (issue !== undefined) {
      issues[field.id] = issue
    }
  }
  return issues
}
